(function () {
  'use strict';

  if (!window.customElements || !window.PremiottVitrinaSchedule) return;
  if (window.customElements.get('premiott-vitrina')) return;

  var schedule = window.PremiottVitrinaSchedule;

  // Matches the approved prototype's two-phase scroll math: the first slice of
  // the tall .pv-experience scroll distance opens the doors (--pv-entry 0→1),
  // the remaining slice walks the gallery track (--pv-walk 0→1).
  var ENTRY_THRESHOLD = 0.19;
  var RELOAD_GUARD_KEY = 'premiottVitrinaReloaded';

  function clamp(value, minimum, maximum) {
    return Math.min(maximum, Math.max(minimum, value));
  }

  function pad(value) {
    return String(value).padStart(2, '0');
  }

  function setText(elements, value) {
    elements.forEach(function (element) {
      element.textContent = value;
    });
  }

  class PremiottVitrina extends HTMLElement {
    constructor() {
      super();

      this.connected = false;

      this.active = 0;
      this.debugState = null;
      this.debugBar = null;
      this.swayAngle = 0;
      this.lastScrollY = 0;
      this.lastScrollAt = 0;
      this.archiveDragging = false;
      this.archiveStartX = 0;
      this.archiveStartScroll = 0;
      this.hoveredShell = null;
      this.pendingPointerEvent = null;

      this.frameId = 0;
      this.swayFrame = 0;
      this.pointerFrame = 0;
      this.roomShiftFrame = 0;
      this.roomShiftTimer = 0;
      this.clockInterval = 0;
      this.snapTimer = 0;
      this.programmaticClear = 0;
      this.programmaticScroll = false;
      this.snapCorrections = 0;
      this.snapLastY = 0;
      this.snapLastAt = 0;
      this.snapPending = false;
      this.touching = false;

      this.handleScroll = this.handleScroll.bind(this);
      this.queueRender = this.queueRender.bind(this);
      this.renderFrame = this.renderFrame.bind(this);
      this.settleSway = this.settleSway.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleEnterClick = this.handleEnterClick.bind(this);
      this.handlePrevClick = this.handlePrevClick.bind(this);
      this.handleNextClick = this.handleNextClick.bind(this);
      this.handleRoomPointerMove = this.handleRoomPointerMove.bind(this);
      this.handleRoomPointerLeave = this.handleRoomPointerLeave.bind(this);
      this.handleArchivePrevClick = this.handleArchivePrevClick.bind(this);
      this.handleArchiveNextClick = this.handleArchiveNextClick.bind(this);
      this.handleArchivePointerDown = this.handleArchivePointerDown.bind(this);
      this.handleArchivePointerMove = this.handleArchivePointerMove.bind(this);
      this.handleArchivePointerUp = this.handleArchivePointerUp.bind(this);
      this.handleRootClick = this.handleRootClick.bind(this);
      this.handleSectionLoad = this.handleSectionLoad.bind(this);
      this.handleMediaChange = this.handleMediaChange.bind(this);
      this.tick = this.tick.bind(this);
      this.snapToNearest = this.snapToNearest.bind(this);
      this.handleTouchStart = this.handleTouchStart.bind(this);
      this.handleTouchEnd = this.handleTouchEnd.bind(this);
    }

    connectedCallback() {
      if (this.connected) return;
      this.connected = true;

      this.classList.add('is-enhanced');
      this.collectRefs();

      this.reducedMotion = window.matchMedia
        ? window.matchMedia('(prefers-reduced-motion: reduce)')
        : { matches: false };
      this.finePointer = window.matchMedia
        ? window.matchMedia('(pointer: fine)')
        : { matches: false };
      this.mobileMQ = window.matchMedia
        ? window.matchMedia('(max-width: 820px)')
        : { matches: false };
      this.mobile = this.mobileMQ.matches;

      this.lastScrollY = window.scrollY || window.pageYOffset || 0;
      this.lastScrollAt = window.performance ? window.performance.now() : Date.now();

      window.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.queueRender, { passive: true });
      window.addEventListener('touchstart', this.handleTouchStart, { passive: true });
      window.addEventListener('touchend', this.handleTouchEnd, { passive: true });
      window.addEventListener('keydown', this.handleKeydown);
      document.addEventListener('shopify:section:load', this.handleSectionLoad);
      this.addMediaListener(this.reducedMotion);
      this.addMediaListener(this.finePointer);
      this.addMediaListener(this.mobileMQ);

      if (this.room) {
        this.room.addEventListener('pointermove', this.handleRoomPointerMove, { passive: true });
        this.room.addEventListener('pointerleave', this.handleRoomPointerLeave);
      }

      if (this.enterButton) this.enterButton.addEventListener('click', this.handleEnterClick);
      if (this.prevButton) this.prevButton.addEventListener('click', this.handlePrevClick);
      if (this.nextButton) this.nextButton.addEventListener('click', this.handleNextClick);

      if (this.archiveRail) {
        this.archiveRail.addEventListener('pointerdown', this.handleArchivePointerDown);
        this.archiveRail.addEventListener('pointermove', this.handleArchivePointerMove);
        this.archiveRail.addEventListener('pointerup', this.handleArchivePointerUp);
        this.archiveRail.addEventListener('pointercancel', this.handleArchivePointerUp);
      }
      if (this.archivePrev) this.archivePrev.addEventListener('click', this.handleArchivePrevClick);
      if (this.archiveNext) this.archiveNext.addEventListener('click', this.handleArchiveNextClick);

      this.addEventListener('click', this.handleRootClick);

      this.buildEngravingRings();
      this.maybeBuildDebugToggle();
      this.syncState();
      this.syncCountdown();
      this.clockInterval = window.setInterval(this.tick, 1000);

      this.renderFrame();
    }

    disconnectedCallback() {
      if (!this.connected) return;
      this.connected = false;

      window.clearInterval(this.clockInterval);
      window.clearTimeout(this.snapTimer);
      window.clearTimeout(this.programmaticClear);
      window.clearTimeout(this.roomShiftTimer);
      window.cancelAnimationFrame(this.frameId);
      window.cancelAnimationFrame(this.swayFrame);
      window.cancelAnimationFrame(this.pointerFrame);
      window.cancelAnimationFrame(this.roomShiftFrame);

      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.queueRender);
      window.removeEventListener('touchstart', this.handleTouchStart);
      window.removeEventListener('touchend', this.handleTouchEnd);
      window.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener('shopify:section:load', this.handleSectionLoad);
      this.removeMediaListener(this.reducedMotion);
      this.removeMediaListener(this.finePointer);
      this.removeMediaListener(this.mobileMQ);

      if (this.room) {
        this.room.removeEventListener('pointermove', this.handleRoomPointerMove);
        this.room.removeEventListener('pointerleave', this.handleRoomPointerLeave);
      }

      if (this.enterButton) this.enterButton.removeEventListener('click', this.handleEnterClick);
      if (this.prevButton) this.prevButton.removeEventListener('click', this.handlePrevClick);
      if (this.nextButton) this.nextButton.removeEventListener('click', this.handleNextClick);

      if (this.archiveRail) {
        this.archiveRail.removeEventListener('pointerdown', this.handleArchivePointerDown);
        this.archiveRail.removeEventListener('pointermove', this.handleArchivePointerMove);
        this.archiveRail.removeEventListener('pointerup', this.handleArchivePointerUp);
        this.archiveRail.removeEventListener('pointercancel', this.handleArchivePointerUp);
      }
      if (this.archivePrev) this.archivePrev.removeEventListener('click', this.handleArchivePrevClick);
      if (this.archiveNext) this.archiveNext.removeEventListener('click', this.handleArchiveNextClick);

      this.removeEventListener('click', this.handleRootClick);

      this.clearShellHover();

      if (this.debugBar && this.debugBar.parentNode) {
        this.debugBar.parentNode.removeChild(this.debugBar);
      }
      this.debugBar = null;
    }

    // ---- setup -------------------------------------------------------

    collectRefs() {
      this.experience = this.querySelector('.pv-experience');
      this.room = this.querySelector('[data-vitrina-room]');
      this.exhibits = Array.prototype.slice.call(this.querySelectorAll('[data-vitrina-product]'));
      this.hasExhibits = this.exhibits.length > 0;

      this.enterButton = this.querySelector('[data-vitrina-enter]');
      this.prevButton = this.querySelector('[data-vitrina-prev]');
      this.nextButton = this.querySelector('[data-vitrina-next]');
      this.exhibitCurrent = this.querySelector('[data-vitrina-exhibit-current]');
      this.exhibitTotal = this.querySelector('[data-vitrina-exhibit-total]');

      this.countdownEls = Array.prototype.slice.call(this.querySelectorAll('[data-vitrina-countdown]'));
      this.cdDays = Array.prototype.slice.call(this.querySelectorAll('[data-vitrina-cd-days]'));
      this.cdHours = Array.prototype.slice.call(this.querySelectorAll('[data-vitrina-cd-hours]'));
      this.cdMinutes = Array.prototype.slice.call(this.querySelectorAll('[data-vitrina-cd-minutes]'));
      this.cdSeconds = Array.prototype.slice.call(this.querySelectorAll('[data-vitrina-cd-seconds]'));

      this.archiveRail = this.querySelector('[data-vitrina-archive-rail]');
      this.archivePrev = this.querySelector('[data-vitrina-archive-prev]');
      this.archiveNext = this.querySelector('[data-vitrina-archive-next]');

      this.links = Array.prototype.slice.call(this.querySelectorAll('[data-vitrina-link]'));
      this.purchaseCtas = Array.prototype.slice.call(this.querySelectorAll('[data-vitrina-purchase-cta]'));
      this.closedCtas = Array.prototype.slice.call(this.querySelectorAll('[data-vitrina-closed-cta]'));

      this.eventStory = this.querySelector('.pv-event-story');
      this.eventCards = this.querySelector('.pv-event-cards');
    }

    addMediaListener(media) {
      if (!media) return;
      if (media.addEventListener) media.addEventListener('change', this.handleMediaChange);
      else if (media.addListener) media.addListener(this.handleMediaChange);
    }

    removeMediaListener(media) {
      if (!media) return;
      if (media.removeEventListener) media.removeEventListener('change', this.handleMediaChange);
      else if (media.removeListener) media.removeListener(this.handleMediaChange);
    }

    // ---- scroll-scrub: room entry + gallery walk ----------------------

    handleScroll() {
      this.queueRender();
      if (this.hasExhibits) this.kickSway();
      this.scheduleSnap();
    }

    // ---- scroll snapping: never leave the visitor parked mid-animation ----

    handleTouchStart() {
      // Finger down = the visitor is in control; never snap out from under them.
      this.touching = true;
      this.snapPending = false;
      window.clearTimeout(this.snapTimer);
    }

    handleTouchEnd() {
      // Momentum starts now. Reset the velocity clock so the first inertial
      // frame is treated as "still moving" (no premature snap on release).
      this.touching = false;
      this.snapLastAt = 0;
      this.scheduleSnap();
    }

    scheduleSnap() {
      if (this.mobile) return; // mobile scrolls normally — no snapping
      if (this.programmaticScroll || this.touching || this.reducedMotion.matches) return;
      if (!this.hasExhibits || this.dataset.state !== 'open') return;

      var now = window.performance ? window.performance.now() : Date.now();
      var y = window.scrollY || window.pageYOffset || 0;
      var dt = now - this.snapLastAt;
      // First event after an idle gap is treated as fast (avoid snapping the
      // instant the visitor starts scrolling).
      var speed = dt > 0 && dt < 250 ? Math.abs(y - this.snapLastY) / dt : 1;
      this.snapLastY = y;
      this.snapLastAt = now;
      this.snapCorrections = 0;

      if (speed < 0.12) {
        // Decelerated enough: take over now instead of waiting out the full
        // momentum tail. Let the scheduled snap ride rather than pushing it back
        // on every slow inertial tick.
        if (!this.snapPending) {
          this.snapPending = true;
          window.clearTimeout(this.snapTimer);
          this.snapTimer = window.setTimeout(this.snapToNearest, 60);
        }
      } else {
        // Still flinging fast: cancel any pending quick snap and re-check soon.
        this.snapPending = false;
        window.clearTimeout(this.snapTimer);
        this.snapTimer = window.setTimeout(this.snapToNearest, 110);
      }
    }

    snapToNearest() {
      this.snapPending = false;
      if (this.reducedMotion.matches) return;
      if (!this.hasExhibits || this.dataset.state !== 'open' || !this.experience) return;

      var rect = this.experience.getBoundingClientRect();
      // Only snap while the scroll-jack fills the viewport (i.e. inside the walk).
      if (rect.top > 0 || rect.bottom < window.innerHeight) return;

      var distance = this.experience.offsetHeight - window.innerHeight;
      if (distance <= 0) return;
      var progress = clamp(-rect.top / distance, 0, 1);

      var lastIndex = this.exhibits.length - 1;
      var points = [0];
      for (var i = 0; i <= lastIndex; i += 1) {
        points.push(ENTRY_THRESHOLD + (lastIndex > 0 ? i / lastIndex : 0) * (1 - ENTRY_THRESHOLD));
      }

      var nearest = points[0];
      var bestGap = Infinity;
      points.forEach(function (point) {
        var gap = Math.abs(point - progress);
        if (gap < bestGap) { bestGap = gap; nearest = point; }
      });

      if (bestGap < 0.01) return;
      this.beginProgrammaticScroll(this.experienceTop() + distance * nearest);
    }

    experienceTop() {
      return this.experience.getBoundingClientRect().top + (window.scrollY || window.pageYOffset || 0);
    }

    beginProgrammaticScroll(top) {
      this.programmaticScroll = true;
      this.snapPending = false;
      window.clearTimeout(this.programmaticClear);
      var self = this;
      this.programmaticClear = window.setTimeout(function () {
        self.programmaticScroll = false;
        // The theme's on-scroll-up header can shift layout mid-snap; re-check and
        // correct (up to twice) so we always finish exactly on a snap point.
        if (self.connected && self.snapCorrections < 2) {
          self.snapCorrections += 1;
          self.snapToNearest();
        }
      }, 620);
      window.scrollTo({ top: top, behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
    }

    queueRender() {
      if (this.frameId) return;
      this.frameId = window.requestAnimationFrame(this.renderFrame);
    }

    renderFrame() {
      this.frameId = 0;
      this.updateGuideProgress();
      if (this.hasExhibits && this.dataset.state !== 'results') this.updateGallery();
    }

    updateGallery() {
      // Mobile: no scroll-jack. The gallery is normal document flow; only the
      // doors are scroll-linked — they part over the first ~0.82 screens of
      // scroll, then the visitor simply scrolls down through the pieces.
      if (this.mobile) {
        var scrollY = window.scrollY || window.pageYOffset || 0;
        var viewport = window.innerHeight || 1;
        var doorEntry = clamp(scrollY / (viewport * 0.82), 0, 1);
        this.style.setProperty('--pv-entry', doorEntry.toFixed(4));
        this.classList.toggle('is-entered', doorEntry >= 0.999);
        return;
      }

      var lastIndex = this.exhibits.length - 1;
      var rect = this.experience.getBoundingClientRect();
      var distance = Math.max(1, this.experience.offsetHeight - window.innerHeight);
      var progress = clamp(-rect.top / distance, 0, 1);
      var entry = Math.min(1, progress / ENTRY_THRESHOLD);
      var walk = clamp((progress - ENTRY_THRESHOLD) / (1 - ENTRY_THRESHOLD), 0, 1);
      var roomTop = this.room ? Math.max(0, this.room.getBoundingClientRect().top) : 0;
      var active = clamp(Math.round(walk * lastIndex), 0, lastIndex);

      this.style.setProperty('--pv-room-top', roomTop.toFixed(2) + 'px');
      this.style.setProperty('--pv-entry', entry.toFixed(4));
      this.style.setProperty('--pv-walk', walk.toFixed(4));
      this.style.setProperty('--pv-active', String(active));
      this.classList.toggle('is-entered', entry >= 1);

      if (active !== this.active) {
        this.active = active;
        this.pulseRoom();
      }

      if (this.exhibitCurrent) this.exhibitCurrent.textContent = String(active + 1);
      if (this.exhibitTotal) this.exhibitTotal.textContent = String(this.exhibits.length);

      this.exhibits.forEach(function (exhibit, index) {
        var focus = Math.max(0, 1 - Math.abs(walk * lastIndex - index));
        exhibit.style.setProperty('--pv-local-focus', focus.toFixed(3));
      });
    }

    updateGuideProgress() {
      if (this.eventStory) {
        var storyRect = this.eventStory.getBoundingClientRect();
        var storyDistance = Math.max(1, this.eventStory.offsetHeight + window.innerHeight);
        var guideProgress = clamp((window.innerHeight - storyRect.top) / storyDistance, 0, 1);
        this.style.setProperty('--pv-guide-progress', guideProgress.toFixed(3));
      }

      if (this.eventCards && this.eventCards.lastElementChild) {
        var cardsRect = this.eventCards.getBoundingClientRect();
        var lastRect = this.eventCards.lastElementChild.getBoundingClientRect();
        var finalCenter = lastRect.top - cardsRect.top + lastRect.height * 0.5;
        var phaseDistance = Math.max(1, window.innerHeight * 0.2 + finalCenter);
        var timelineProgress = clamp((window.innerHeight * 0.78 - cardsRect.top) / phaseDistance, 0, 1);
        this.style.setProperty('--pv-timeline-progress', timelineProgress.toFixed(3));
      }
    }

    pulseRoom() {
      window.clearTimeout(this.roomShiftTimer);
      window.cancelAnimationFrame(this.roomShiftFrame);
      this.classList.remove('is-room-shifting');

      var self = this;
      this.roomShiftFrame = window.requestAnimationFrame(function () {
        self.classList.add('is-room-shifting');
        self.roomShiftTimer = window.setTimeout(function () {
          self.classList.remove('is-room-shifting');
        }, 520);
      });
    }

    goTo(index) {
      if (!this.hasExhibits || !this.experience) return;

      var lastIndex = this.exhibits.length - 1;
      var clamped = clamp(index, 0, lastIndex);
      var distance = this.experience.offsetHeight - window.innerHeight;
      if (distance <= 0) return;

      var progress = lastIndex > 0
        ? ENTRY_THRESHOLD + (clamped / lastIndex) * (1 - ENTRY_THRESHOLD)
        : ENTRY_THRESHOLD;

      this.beginProgrammaticScroll(this.experienceTop() + distance * progress);
    }

    handleEnterClick() {
      this.goTo(0);
    }

    handlePrevClick() {
      this.goTo(this.active - 1);
    }

    handleNextClick() {
      this.goTo(this.active + 1);
    }

    handleKeydown(event) {
      if (!this.hasExhibits) return;
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;

      var target = event.target;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable)) {
        return;
      }

      if (event.key === 'ArrowLeft') this.goTo(this.active - 1);
      else if (event.key === 'ArrowRight') this.goTo(this.active + 1);
    }

    // ---- scroll-velocity frame sway, damped settle --------------------

    kickSway() {
      if (this.reducedMotion.matches || this.dataset.state === 'results') return;
      // Touch devices skip the frame sway: it writes --pv-hang-sway to every
      // exhibit each scroll frame, which is pure cost on mobile GPUs.
      if (!this.finePointer.matches) return;

      var now = window.performance ? window.performance.now() : Date.now();
      var scrollY = window.scrollY || window.pageYOffset || 0;
      var delta = scrollY - this.lastScrollY;
      var elapsed = clamp(now - this.lastScrollAt, 8, 90);
      this.lastScrollY = scrollY;
      this.lastScrollAt = now;

      if (Math.abs(delta) < 0.5) return;

      var speed = delta / elapsed;
      var impulse = clamp(speed * 0.32, -3.8, 3.8);
      this.swayAngle = clamp(this.swayAngle + impulse, -4.8, 4.8);
      this.applySway();

      if (!this.swayFrame) this.swayFrame = window.requestAnimationFrame(this.settleSway);
    }

    applySway() {
      var angle = this.swayAngle;
      this.exhibits.forEach(function (exhibit, index) {
        var weight = 1 - index * 0.035;
        exhibit.style.setProperty('--pv-hang-sway', (angle * weight).toFixed(3) + 'deg');
      });
    }

    settleSway() {
      this.swayAngle *= 0.972;
      this.applySway();

      if (Math.abs(this.swayAngle) > 0.012) {
        this.swayFrame = window.requestAnimationFrame(this.settleSway);
      } else {
        this.swayAngle = 0;
        this.swayFrame = 0;
        this.applySway();
      }
    }

    resetSway() {
      window.cancelAnimationFrame(this.swayFrame);
      this.swayFrame = 0;
      this.swayAngle = 0;
      this.applySway();
    }

    // ---- pointer spotlight + hover tilt/glass --------------------------

    handleRoomPointerMove(event) {
      if (!this.finePointer.matches || this.reducedMotion.matches) return;

      this.pendingPointerEvent = event;
      if (this.pointerFrame) return;

      var self = this;
      this.pointerFrame = window.requestAnimationFrame(function () {
        self.pointerFrame = 0;
        self.applyPointerEffects(self.pendingPointerEvent);
      });
    }

    applyPointerEffects(event) {
      if (!event) return;

      this.style.setProperty('--pv-light-x', (event.clientX / window.innerWidth * 100).toFixed(1) + '%');
      this.style.setProperty('--pv-light-y', (event.clientY / window.innerHeight * 100).toFixed(1) + '%');
      this.style.setProperty('--pv-spot-x', event.clientX.toFixed(0) + 'px');
      this.style.setProperty('--pv-spot-y', event.clientY.toFixed(0) + 'px');

      var shell = event.target && event.target.closest ? event.target.closest('.pv-portrait-shell') : null;
      if (shell && this.contains(shell)) {
        this.setShellHover(shell, event);
      } else if (this.hoveredShell) {
        this.clearShellHover();
      }
    }

    setShellHover(shell, event) {
      var exhibit = shell.closest('[data-vitrina-product]');

      if (shell !== this.hoveredShell) {
        if (this.hoveredShell) this.clearShellHover();
        this.hoveredShell = shell;
        shell.classList.add('is-hovered');
        if (this.room) this.room.classList.add('is-frame-hovered');
        if (exhibit) exhibit.style.setProperty('--pv-hover-lift', '15px');
      }

      if (!exhibit) return;
      var rect = shell.getBoundingClientRect();
      var x = clamp((event.clientX - rect.left) / rect.width * 2 - 1, -1, 1);
      var y = clamp((event.clientY - rect.top) / rect.height * 2 - 1, -1, 1);
      exhibit.style.setProperty('--pv-hover-tilt-x', (-y * 1.8).toFixed(2) + 'deg');
      exhibit.style.setProperty('--pv-hover-tilt-y', (x * 2.2).toFixed(2) + 'deg');
      exhibit.style.setProperty('--pv-glass-shift', (x * 10).toFixed(1) + 'px');
    }

    clearShellHover() {
      var shell = this.hoveredShell;
      this.hoveredShell = null;
      if (!shell) return;

      shell.classList.remove('is-hovered');
      if (this.room) this.room.classList.remove('is-frame-hovered');

      var exhibit = shell.closest('[data-vitrina-product]');
      if (exhibit) {
        exhibit.style.setProperty('--pv-hover-tilt-x', '0deg');
        exhibit.style.setProperty('--pv-hover-tilt-y', '0deg');
        exhibit.style.setProperty('--pv-glass-shift', '0px');
        exhibit.style.setProperty('--pv-hover-lift', '0px');
      }
    }

    resetSpotlight() {
      this.style.setProperty('--pv-light-x', '50%');
      this.style.setProperty('--pv-light-y', '24%');
      this.style.setProperty('--pv-spot-x', (window.innerWidth / 2).toFixed(0) + 'px');
      this.style.setProperty('--pv-spot-y', (window.innerHeight / 2).toFixed(0) + 'px');
    }

    handleRoomPointerLeave() {
      this.resetSpotlight();
      this.clearShellHover();
    }

    handleMediaChange() {
      if (this.mobileMQ) this.mobile = this.mobileMQ.matches;
      if (this.reducedMotion.matches || !this.finePointer.matches) {
        this.resetSpotlight();
        this.clearShellHover();
        this.resetSway();
      }
      this.queueRender();
    }

    // ---- countdown + state reconcile -----------------------------------

    tick() {
      this.syncState();
      this.syncCountdown();
    }

    syncCountdown() {
      var forced = this.dataset.stateOverride !== 'automatic';
      this.countdownEls.forEach(function (element) {
        element.hidden = forced;
      });

      var remaining = schedule.remaining(new Date());
      var seconds = remaining.totalSeconds % 60;

      setText(this.cdDays, pad(remaining.days));
      setText(this.cdHours, pad(remaining.hours));
      setText(this.cdMinutes, pad(remaining.minutes));
      setText(this.cdSeconds, pad(seconds));
    }

    syncState() {
      var now = new Date();
      var override = this.dataset.stateOverride;
      var state;
      var sealed;

      if (this.debugState) {
        state = this.debugState === 'open' ? 'open' : 'results';
        sealed = this.debugState === 'sealed';
      } else {
        state = schedule.stateAt(now, override);
        sealed = override === 'automatic' ? schedule.isSealed(now) : false;
      }

      this.dataset.state = state;
      this.dataset.sealed = sealed ? 'true' : 'false';

      this.links.forEach(function (link) {
        var product = link.closest('[data-vitrina-product]');
        var available = !!product && product.dataset.productAvailable === 'true';
        var enabled = state === 'open' && available;

        if (enabled) {
          link.setAttribute('href', link.dataset.href);
          link.removeAttribute('aria-disabled');
          link.removeAttribute('tabindex');
        } else {
          link.removeAttribute('href');
          link.setAttribute('aria-disabled', 'true');
          link.setAttribute('tabindex', '-1');
        }
      });

      this.purchaseCtas.forEach(function (cta) {
        cta.hidden = state !== 'open';
      });

      this.closedCtas.forEach(function (cta) {
        cta.hidden = state === 'open';
      });

      if (state === 'results') {
        this.resetSway();
        this.style.setProperty('--pv-entry', '0');
        this.style.setProperty('--pv-walk', '0');
        this.style.setProperty('--pv-active', '0');
        this.active = 0;
      }

      if (state === 'open' && this.exhibits.length === 0 && !this.debugState) {
        this.reloadForOpenedEdition();
      }
    }

    reloadForOpenedEdition() {
      try {
        if (window.sessionStorage.getItem(RELOAD_GUARD_KEY)) return;
        window.sessionStorage.setItem(RELOAD_GUARD_KEY, '1');
      } catch (error) {
        return;
      }
      window.location.reload();
    }

    handleSectionLoad(event) {
      if (event.target && !event.target.contains(this)) return;
      this.syncState();
      this.syncCountdown();
      this.queueRender();
    }

    // ---- debug state toggle (URL: ?vitrina_debug) ----------------------

    maybeBuildDebugToggle() {
      var match = null;
      try {
        match = window.location.search.match(/[?&]vitrina_debug(?:=([a-z]+))?/i);
      } catch (error) {
        match = null;
      }
      if (!match) return;

      var bar = document.createElement('div');
      bar.className = 'pv-debug-toggle';
      bar.setAttribute('role', 'group');
      bar.setAttribute('aria-label', 'Vitrina debug state');

      var options = [['auto', 'Auto'], ['open', 'Deschis'], ['results', 'Închis'], ['sealed', 'Sealed']];
      var self = this;
      options.forEach(function (option) {
        var button = document.createElement('button');
        button.type = 'button';
        button.textContent = option[1];
        button.dataset.vitrinaDebug = option[0];
        button.addEventListener('click', function () {
          self.setDebugState(option[0]);
        });
        bar.appendChild(button);
      });

      this.debugBar = bar;
      this.appendChild(bar);

      var requested = match[1] ? match[1].toLowerCase() : 'auto';
      if (requested !== 'open' && requested !== 'results' && requested !== 'sealed') requested = 'auto';
      this.debugState = requested === 'auto' ? null : requested;
      this.updateDebugButtons(requested);
    }

    setDebugState(value) {
      this.debugState = value === 'auto' ? null : value;
      this.updateDebugButtons(value);
      this.syncState();
      this.syncCountdown();
      this.queueRender();
    }

    updateDebugButtons(active) {
      if (!this.debugBar) return;
      var buttons = this.debugBar.querySelectorAll('[data-vitrina-debug]');
      Array.prototype.forEach.call(buttons, function (button) {
        button.setAttribute('aria-pressed', String(button.dataset.vitrinaDebug === active));
      });
    }

    // ---- direct checkout -------------------------------------------------

    handleRootClick(event) {
      // Quick-view close: the × button, or a click on the dialog backdrop
      // (the dialog element itself, outside its panel).
      var closeEl = event.target.closest ? event.target.closest('[data-vitrina-quickview-close]') : null;
      if (closeEl) {
        var dialogToClose = closeEl.closest('dialog');
        if (dialogToClose) dialogToClose.close();
        return;
      }
      if (event.target.matches && event.target.matches('[data-vitrina-quickview-dialog]')) {
        event.target.close();
        return;
      }

      var link = event.target.closest ? event.target.closest('[data-vitrina-link]') : null;
      if (!link) return;

      // Watch image opens the quick-view — but only while the window is open.
      // (In the closed/results state the gallery is hidden, so this never fires.)
      if (link.hasAttribute('data-vitrina-quickview') && this.dataset.state === 'open') {
        this.handleQuickviewClick(event, link);
        return;
      }

      if (link.getAttribute('aria-disabled') === 'true') {
        event.preventDefault();
        return;
      }

      if (link.hasAttribute('data-vitrina-purchase-cta')) {
        this.handlePurchaseClick(event, link);
      }
    }

    handleQuickviewClick(event, link) {
      event.preventDefault();
      var exhibit = link.closest('[data-vitrina-product]');
      var dialog = exhibit ? exhibit.querySelector('[data-vitrina-quickview-dialog]') : null;
      if (!dialog || typeof dialog.showModal !== 'function') {
        if (link.dataset.href) window.location.href = link.dataset.href;
        return;
      }
      dialog.showModal();
      var previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      var restore = function () {
        document.body.style.overflow = previousOverflow;
        dialog.removeEventListener('close', restore);
      };
      dialog.addEventListener('close', restore);
    }

    handlePurchaseClick(event, link) {
      event.preventDefault();

      var fallbackHref = link.dataset.href;
      var variantId = Number(link.dataset.variantId);

      if (!variantId) {
        window.location.href = fallbackHref;
        return;
      }

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      })
        .then(function (response) {
          if (!response.ok) throw new Error('premiott-vitrina: cart add failed');
          window.location.href = '/checkout';
        })
        .catch(function () {
          window.location.href = fallbackHref;
        });
    }

    // ---- archive rail ------------------------------------------------------

    handleArchivePrevClick() {
      if (!this.archiveRail) return;
      this.archiveRail.scrollBy({
        left: -this.archiveRail.clientWidth * 0.72,
        behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
      });
    }

    handleArchiveNextClick() {
      if (!this.archiveRail) return;
      this.archiveRail.scrollBy({
        left: this.archiveRail.clientWidth * 0.72,
        behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
      });
    }

    handleArchivePointerDown(event) {
      this.archiveDragging = true;
      this.archiveStartX = event.clientX;
      this.archiveStartScroll = this.archiveRail.scrollLeft;
      this.archiveRail.classList.add('is-dragging');
      if (this.archiveRail.setPointerCapture) {
        try {
          this.archiveRail.setPointerCapture(event.pointerId);
        } catch (error) {
          // pointer capture is best-effort
        }
      }
    }

    handleArchivePointerMove(event) {
      if (!this.archiveDragging) return;
      this.archiveRail.scrollLeft = this.archiveStartScroll - (event.clientX - this.archiveStartX) * 1.15;
    }

    handleArchivePointerUp() {
      this.archiveDragging = false;
      if (this.archiveRail) this.archiveRail.classList.remove('is-dragging');
    }

    // ---- circular engraving ------------------------------------------------

    buildEngravingRings() {
      var rings = Array.prototype.slice.call(this.querySelectorAll('[data-vitrina-engraving]'));

      rings.forEach(function (ring) {
        var text = ring.dataset.vitrinaEngraving || '';
        var characters = Array.from(text);

        var fragments = characters.map(function (character, index) {
          var span = document.createElement('span');
          span.textContent = character === ' ' ? ' ' : character;
          span.style.setProperty('--pv-ring-index', index);
          span.style.setProperty('--pv-ring-count', characters.length);
          return span;
        });

        if (ring.replaceChildren) {
          ring.replaceChildren.apply(ring, fragments);
        } else {
          ring.innerHTML = '';
          fragments.forEach(function (fragment) {
            ring.appendChild(fragment);
          });
        }
      });
    }
  }

  window.customElements.define('premiott-vitrina', PremiottVitrina);
})();
